import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bell, ChartBar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PurchaseOrdersPanel from '@/components/PurchaseOrdersPanel';
import ApprovalsPanel from '@/components/ops/ApprovalsPanel';
import VendorOrdersPanel from '@/components/ops/VendorOrdersPanel';
import ProjectCodesPanel from '@/components/ops/ProjectCodesPanel';
import SpendingAnalyticsPanel from '@/components/ops/SpendingAnalyticsPanel';
import VendorSelectionDialog from '@/components/ops/VendorSelectionDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OperationsManagerDashboard = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState([]);
  const [allQuotations, setAllQuotations] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [vendorOrders, setVendorOrders] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [selectedReq, setSelectedReq] = useState(null);
  const [checkingReminders, setCheckingReminders] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [reqRes, pcRes, voRes, analyticsRes, allQuotRes] = await Promise.all([
        axios.get(`${API}/requisitions`, { headers: getAuthHeader() }),
        axios.get(`${API}/project-codes`, { headers: getAuthHeader() }),
        axios.get(`${API}/vendor-orders`, { headers: getAuthHeader() }),
        axios.get(`${API}/analytics/project-spending`, { headers: getAuthHeader() }),
        axios.get(`${API}/quotations`, { headers: getAuthHeader() })
      ]);
      setRequisitions(reqRes.data);
      setProjectCodes(pcRes.data);
      setVendorOrders(voRes.data);
      setAnalytics(analyticsRes.data);
      setAllQuotations(allQuotRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleMarkReady = async (orderId) => {
    try {
      await axios.post(`${API}/vendor-orders/${orderId}/finalize`, {}, { headers: getAuthHeader() });
      toast.success('Order marked ready for PO');
      loadData();
    } catch (error) {
      toast.error('Failed to mark order ready');
    }
  };

  const handleMarkReadyForSelection = async (reqId) => {
    try {
      await axios.post(`${API}/requisitions/${reqId}/mark-ready-for-selection`, {}, { headers: getAuthHeader() });
      toast.success('Requisition moved to vendor selection');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move requisition');
    }
  };

  const handleCheckReminders = async () => {
    setCheckingReminders(true);
    try {
      const response = await axios.post(`${API}/check-order-deadlines`, {}, { headers: getAuthHeader() });
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error('Failed to check reminders');
    } finally {
      setCheckingReminders(false);
    }
  };

  const pendingApproval = requisitions.filter(r => r.status === 'quotations_added');
  const awaitingQuotes = requisitions.filter(r => r.status === 'pending_quotation');
  const quotCounts = useMemo(() => {
    const counts = {};
    allQuotations.forEach((q) => { counts[q.requisition_id] = (counts[q.requisition_id] || 0) + 1; });
    return counts;
  }, [allQuotations]);

  // Build a duplicate map so Ops can see "N similar requisitions" for each pending item and
  // decide whether it's truly redundant. Match is strict: identical normalized catalogue
  // number (case-insensitive, punctuation-stripped). Employees are no longer blocked on the
  // client — Ops is the source of truth for redundancy.
  const duplicateMap = useMemo(() => {
    const normCat = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    const map = new Map();
    requisitions.forEach((r) => {
      const key = normCat(r.catalogue_number);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    const out = {};
    requisitions.forEach((r) => {
      const key = normCat(r.catalogue_number);
      if (!key) return;
      const others = (map.get(key) || []).filter((o) => o.id !== r.id);
      if (others.length > 0) out[r.id] = others;
    });
    return out;
  }, [requisitions]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-text-primary">
              Operations Dashboard
            </h1>
            <p className="mt-2 text-text-secondary">Manage vendor approvals, project codes, and analytics</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleCheckReminders}
              disabled={checkingReminders}
              data-testid="check-reminders-button"
            >
              <Bell size={20} className="mr-2" weight="duotone" />
              {checkingReminders ? 'Checking...' : 'Check Reminders'}
            </Button>
            <Button
              className="bg-accent-brand hover:bg-accent-brand-hover text-white"
              onClick={() => navigate('/vendor-performance')}
              data-testid="vendor-performance-button"
            >
              <ChartBar size={20} className="mr-2" weight="duotone" />
              Vendor Performance
            </Button>
          </div>
        </div>

        <Tabs defaultValue="approvals" className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-5">
            <TabsTrigger value="approvals" data-testid="approvals-tab">Approvals ({pendingApproval.length})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab">Vendor Orders</TabsTrigger>
            <TabsTrigger value="pos" data-testid="ops-pos-tab">POs</TabsTrigger>
            <TabsTrigger value="projects" data-testid="projects-tab">Project Codes</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="approvals" className="mt-6">
            <ApprovalsPanel
              pendingApproval={pendingApproval}
              awaitingQuotes={awaitingQuotes}
              quotCounts={quotCounts}
              duplicateMap={duplicateMap}
              onReview={setSelectedReq}
              onProceed={handleMarkReadyForSelection}
              onDeleted={loadData}
            />
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <VendorOrdersPanel vendorOrders={vendorOrders} onMarkReady={handleMarkReady} />
          </TabsContent>

          <TabsContent value="pos" className="mt-6">
            <PurchaseOrdersPanel />
          </TabsContent>

          <TabsContent value="projects" className="mt-6">
            <ProjectCodesPanel projectCodes={projectCodes} onCreated={loadData} />
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <SpendingAnalyticsPanel analytics={analytics} />
          </TabsContent>
        </Tabs>

        {selectedReq && (
          <VendorSelectionDialog
            req={selectedReq}
            onClose={() => setSelectedReq(null)}
            onChanged={loadData}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default OperationsManagerDashboard;
